import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import API_BASE_URL from "./config";
import "./Signup.css";


const Signup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    otp_code: ""
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpNote, setOtpNote] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(""); // Clear error when user starts typing
  };

  const validateForm = () => {
    // Check if all required fields are filled
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setError("Email and password fields are required");
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }

    // Check if passwords match
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    // If OTP sent, validate OTP code
    if (otpSent && !formData.otp_code) {
      setError("Please enter the OTP code sent to your email");
        return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          otp_code: otpSent ? formData.otp_code : null
        }),
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        // If JSON parsing fails, try to get text response
        const text = await response.text();
        console.error("Failed to parse JSON response:", text);
        setError(`Server error (${response.status}): ${text || "Unknown error"}`);
        setIsLoading(false);
        return;
      }

      if (response.ok) {
        if (data.otp_required && !otpSent) {
          // OTP sent, show OTP input
          setOtpSent(true);
          setOtpNote(data.note || "OTP sent to your email");
          setError("");
        } else {
          // Account created successfully
          navigate("/login", { 
            state: { 
              message: "Account created successfully! Please log in." 
            } 
          });
        }
      } else {
        // Log the full error for debugging
        console.error("Signup error:", {
          status: response.status,
          statusText: response.statusText,
          data: data
        });
        console.error("Full error details:", JSON.stringify(data, null, 2));
        const errorMessage = data.message || data.error || `Failed to create account (${response.status})`;
        console.error("Error message:", errorMessage);
        setError(errorMessage);
      }
    } catch (error) {
      console.error("Network error during signup:", error);
      setError(`Network error: ${error.message || "Please try again."}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="auth-illustration">
        <div className="brand">
          <img className="brand-logo-img" src={`${process.env.PUBLIC_URL || ''}/assets/wetano-logo.png`} alt="WeTano Solutions" />
          <div className="brand-text">
            <h2>WeTano Solutions</h2>
            <p>Unlock full control over your chatbot</p>
          </div>
        </div>
        <ul className="highlights">
          <li>Manage conversations and files</li>
          <li>Customize bot experience</li>
          <li>Secure, role-based access</li>
        </ul>
      </div>

      <div className="signup-card">
        <div className="brand-mini">
          <img src={`${process.env.PUBLIC_URL || ''}/assets/wetano-logo.png`} alt="WeTano Solutions" />
          <span>WeTano Solutions</span>
        </div>
        <div className="signup-header">
          <h1>Get started</h1>
          <p>Create your organization admin account</p>
          <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '5px' }}>
            Use an email with your organization's domain
          </p>
        </div>

        <form onSubmit={handleSubmit} className="signup-form">
          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="name@yourdomain.com"
              required
              disabled={isLoading || otpSent}
            />
            <small style={{ color: '#6b7280', fontSize: '12px' }}>Must match your organization's domain</small>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password *</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Min 6 characters"
              required
              disabled={isLoading || otpSent}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password *</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter your password"
              required
              disabled={isLoading || otpSent}
            />
          </div>

          {otpSent && (
            <div className="form-group">
              <label htmlFor="otp_code">OTP Code *</label>
              <input
                type="text"
                id="otp_code"
                name="otp_code"
                value={formData.otp_code}
                onChange={handleChange}
                placeholder="Enter 6-digit OTP"
                required
                disabled={isLoading}
                maxLength="6"
                style={{ letterSpacing: '8px', textAlign: 'center', fontSize: '18px', fontWeight: 'bold' }}
              />
              {otpNote && (
                <div style={{ 
                  background: '#eff6ff', 
                  border: '1px solid #bfdbfe', 
                  borderRadius: '6px', 
                  padding: '8px 12px', 
                  marginTop: '8px',
                  fontSize: '12px',
                  color: '#1e40af'
                }}>
                  {otpNote}
                </div>
              )}
              <small style={{ color: '#6b7280', fontSize: '12px' }}>Check your email for the verification code</small>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <button 
            type="submit" 
            className="signup-button"
            disabled={isLoading}
          >
            {isLoading 
              ? (otpSent ? "Verifying..." : "Sending OTP...") 
              : (otpSent ? "Verify & Create Account" : "Send OTP & Continue")
            }
          </button>
        </form>

        <div className="signup-footer">
          <p>
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;

